<template>
    <div v-if="!currentPage" class="empty-page-message">
        There is nothing here, add new pages to create.
    </div>
    <div v-else class="editor-panel-inner">
        <InputPopup v-model="showRenamePopup" title="Rename Page"
            :fields="[{ key: 'newName', label: 'New Page Name', default: renameInput }]" @confirm="onRenameConfirm" />
        <InfoPopup v-model="showInfoPopup" title="Warning!" :showConfirmButton="showInfoPopupConfirmButton"
            :showCancelButton="showInfoPopupCancelButton" @confirm="onInfoPopupConfirm" @cancel="onInfoPopupCancel">
            <div>{{ infoPopupMessage }}</div>
        </InfoPopup>
        <div class="editor-header">
            <button class="modify-page-btn" @click="renameCurrentPage" aria-label="Rename page">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M4 21h4l11-11-4-4L4 17v4zM20.7 7.3c.4-.4.4-1 0-1.4l-2.6-2.6a.9959.9959 0 0 0-1.4 0l-1.8 1.8 4 4 1.8-1.8z" />
                </svg>
            </button>
            <h3>{{ page_name }}</h3>
            <span class="save-indicator">{{ saveStatus }}</span>
            <button class="delete-page-btn" @click="onDeleteCurrentPage" aria-label="Delete page">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M3 6h18v2H3V6zm2 3h14l-1.1 12.1c-.1 1.1-1 1.9-2.1 1.9H8.1c-1.1 0-2-.8-2.1-1.9L5 9zm5-7h4v2h-4V2z" />
                </svg>
            </button>
        </div>

        <button v-if="localLines.length === 0" class="new-line-btn" @click="createLine">
            + New line
        </button>
        <div class="line-list">
            <div v-for="(line, idx) in localLines" :key="line.line_id || idx" class="line-bubble"
                :class="{
                    selected: idx === selectedIndex && !isSelectMode,
                    'select-mode': isSelectMode,
                    'bulk-selected': isLineSelected(idx)
                }" @click="onLineBubbleClick(idx, $event)">
                <div class="line-toolbar">
                    <div class="line-toolbar-meta">
                        <button class="line-check" :class="{ checked: isLineSelected(idx) }"
                            v-if="isSelectMode"
                            :aria-pressed="isLineSelected(idx)" :aria-label="`Select line ${idx + 1}`"
                            @click.stop="toggleLineSelection(idx, $event)">
                            <svg v-if="isLineSelected(idx)" width="14" height="14" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"
                                stroke-linejoin="round" aria-hidden="true">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </button>
                        <span class="ln-num">{{ idx + 1 }}</span>
                        <button v-if="hasLineAudio(line)" class="line-card-action audio-ready-pill"
                            @click.stop="clearAudio(idx)"
                            :aria-label="`Remove audio from line ${idx + 1}`" title="Remove audio from this line">
                            <span>Audio</span>
                            <span class="audio-ready-remove" aria-hidden="true">
                                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2"
                                    stroke-linecap="round">
                                    <path d="M4 4l8 8M12 4l-8 8" />
                                </svg>
                            </span>
                        </button>
                    </div>
                    <div v-if="!isSelectMode" class="line-toolbar-actions">
                        <button class="line-card-action delete-line-action" :disabled="isGenerating"
                            @click.stop="requestSingleDelete(idx)"
                            :aria-label="`Delete line ${idx + 1}`" title="Delete this line">
                            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                <path
                                    d="M3 6h18v2H3V6zm2 3h14l-1.1 12.1c-.1 1.1-1 1.9-2.1 1.9H8.1c-1.1 0-2-.8-2.1-1.9L5 9zm5-7h4v2h-4V2z" />
                            </svg>
                            <span>Delete</span>
                        </button>
                        <button class="insert-btn" v-if="false" @click.stop="insertLine(idx)"
                            aria-label="Insert line below">＋</button>
                    </div>
                </div>
                <div class="line-content">
                    <textarea class="line-textarea" rows="1"
                        :value="localLines[idx].text === 'NULL' ? '' : localLines[idx].text"
                        :readonly="isSelectMode"
                        @input="(e) => { onEdit(idx, e); autoResize(e); }"
                        @keydown.backspace="handleBackspace(idx, $event)" @keydown.enter="handleEnter(idx, $event)"
                        @focus="onTextareaFocus(idx)" @click="onTextareaClick(idx, $event)"
                        placeholder="Type your line here..."></textarea>
                </div>
            </div>
        </div>

        <div class="textarea-toolbar">
            <button v-if="localLines.length > 0" class="line-card-action line-main-action page-all-action"
                :class="pageActionButtonClass"
                :disabled="isPageActionDisabled"
                @click="playAllLines"
                :aria-label="pageActionAriaLabel"
                :title="pageActionTitle">
                <span v-if="isGenerating" class="spinner-line" aria-hidden="true"></span>
                <svg v-else class="play-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M8 5v14l11-7L8 5z" />
                </svg>
                <span class="line-action-label">{{ pageActionLabel }}</span>
            </button>
            <span class="char-count">{{ totalChars }}</span>
            <div class="toolbar-actions">
                <button v-if="!isSelectMode && localLines.length > 0" class="toolbar-btn" :disabled="isGenerating"
                    @click="enterSelectMode">
                    Select
                </button>
                <button v-if="!isSelectMode && localLines.length > 0"
                    class="line-card-action line-main-action toolbar-line-action"
                    :class="lineActionButtonClass(activeLine, selectedIndex)"
                    :disabled="isLineActionDisabled(activeLine, selectedIndex)"
                    @click="playLine(selectedIndex)"
                    :aria-label="lineActionAriaLabel(activeLine, selectedIndex)"
                    :title="lineActionTitle(activeLine, selectedIndex)">
                    <span v-if="isLineGenerating(selectedIndex)" class="spinner-line" aria-hidden="true"></span>
                    <svg v-else class="play-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M8 5v14l11-7L8 5z" />
                    </svg>
                    <span class="line-action-label">{{ lineActionLabel(activeLine, selectedIndex) }}</span>
                </button>
                <template v-else-if="isSelectMode">
                    <span class="selected-count">{{ selectedCount }} selected</span>
                    <button class="toolbar-btn" @click="toggleSelectAll">
                        {{ allLinesSelected ? 'Clear all' : 'Select all' }}
                    </button>
                    <button class="toolbar-btn danger" :disabled="selectedCount === 0 || isGenerating"
                        @click="confirmBulkDelete">
                        Delete
                    </button>
                    <button class="toolbar-btn" @click="exitSelectMode">
                        Cancel
                    </button>
                </template>
            </div>
        </div>
    </div>
</template>
<script>
import { defineComponent, ref, watch, onMounted, computed, nextTick } from 'vue';
import { getPagesByUserId, modifyPageName, deletePageByUserId } from '@/api/ttsProjects';
import { requireAuth } from '@/api/auth';
import { countEditorCharacters } from '@/utils/TTSInputWarnings';
import InputPopup from '@/components/common/InputPopup.vue';
import InfoPopup from '@/components/common/InfoPopup.vue';

export default defineComponent({
    name: 'EditorPanel',
    components: { InputPopup, InfoPopup },
    emits: ['page-changed', 'update:lines', 'select-line', 'delete-line', 'create-line', 'play-line', 'play-all-lines', 'generate-speech'],
    props: {
        pageId: { type: Number, required: true },
        project: { type: Object, required: true },
        saveStatus: { type: String, required: true },
        lines: { type: Array, default: () => [] },
        isGenerating: { type: Boolean, default: false },
        generatingLineIndex: { type: Number, default: null },
        reloadAll: { type: Function },
    },
    setup(props, { emit }) {
        const page_name = ref('');
        const currentPage = ref(null);
        const allPages = ref([]);
        const localLines = ref([]);
        const selectedIndex = ref(0);

        const showRenamePopup = ref(false);
        const renameInput = ref('');

        const showInfoPopup = ref(false);
        const infoPopupMessage = ref('');
        const showInfoPopupConfirmButton = ref(false)
        const showInfoPopupCancelButton = ref(true)
        const nextAction = ref('');
        const isSelectMode = ref(false);
        const selectedLineIndexes = ref(new Set());
        const lastSelectionIndex = ref(null);

        const totalChars = computed(() =>
            localLines.value.reduce((sum, l) => sum + countEditorCharacters(l.text || ''), 0)
        );
        const activeLine = computed(() => localLines.value[selectedIndex.value] || null);
        const readableLines = computed(() => localLines.value.filter(line => !isLineTextEmpty(line)));
        const pageNeedsAudio = computed(() => readableLines.value.some(line => !hasLineAudio(line)));
        const pageActionLabel = computed(() => {
            if (props.isGenerating) return 'Generating';
            return pageNeedsAudio.value ? 'Generate all' : 'Play all';
        });
        const pageActionButtonClass = computed(() => ({
            'needs-audio': pageNeedsAudio.value,
            'has-audio': !pageNeedsAudio.value,
            'is-loading': props.isGenerating,
        }));
        const isPageActionDisabled = computed(() => props.isGenerating || readableLines.value.length === 0);
        const pageActionTitle = computed(() => {
            if (props.isGenerating) return 'Generating audio for this page';
            if (readableLines.value.length === 0) return 'Type text before generating page audio';
            return pageNeedsAudio.value
                ? 'Generate missing audio, then play this page'
                : 'Play all audio on this page';
        });
        const pageActionAriaLabel = computed(() => pageActionTitle.value);
        const selectedCount = computed(() => selectedLineIndexes.value.size);
        const allLinesSelected = computed(() =>
            localLines.value.length > 0 && selectedLineIndexes.value.size === localLines.value.length
        );

        function syncFromProps() {
            const src = Array.isArray(props.lines) ? props.lines : [];
            localLines.value = src.map(l => ({ ...l }));
            if (selectedIndex.value >= localLines.value.length) {
                selectedIndex.value = Math.max(localLines.value.length - 1, 0);
            }
            pruneSelectedIndexes();
        }

        function pushBackToParent() {
            emit('update:lines', localLines.value.map(l => ({ ...l })));
        }

        function isLineSelected(idx) {
            return selectedLineIndexes.value.has(idx);
        }

        function setSelectedLineIndexes(indexes) {
            selectedLineIndexes.value = new Set(
                Array.from(indexes).filter(idx => idx >= 0 && idx < localLines.value.length)
            );
        }

        function pruneSelectedIndexes() {
            setSelectedLineIndexes(selectedLineIndexes.value);
            if (selectedLineIndexes.value.size === 0) {
                lastSelectionIndex.value = null;
            }
            if (localLines.value.length === 0) {
                isSelectMode.value = false;
            }
        }

        function enterSelectMode() {
            if (props.isGenerating) return;
            isSelectMode.value = true;
            setSelectedLineIndexes([]);
            lastSelectionIndex.value = null;
        }

        function exitSelectMode() {
            isSelectMode.value = false;
            setSelectedLineIndexes([]);
            lastSelectionIndex.value = null;
        }

        function toggleLineSelection(idx, event) {
            if (!isSelectMode.value || idx < 0 || idx >= localLines.value.length) return;
            const next = new Set(selectedLineIndexes.value);
            if (event?.shiftKey && lastSelectionIndex.value !== null) {
                const start = Math.min(lastSelectionIndex.value, idx);
                const end = Math.max(lastSelectionIndex.value, idx);
                for (let i = start; i <= end; i += 1) {
                    next.add(i);
                }
            } else if (next.has(idx)) {
                next.delete(idx);
            } else {
                next.add(idx);
            }
            lastSelectionIndex.value = idx;
            setSelectedLineIndexes(next);
        }

        function toggleSelectAll() {
            if (allLinesSelected.value) {
                setSelectedLineIndexes([]);
                lastSelectionIndex.value = null;
                return;
            }
            setSelectedLineIndexes(localLines.value.map((_, idx) => idx));
            lastSelectionIndex.value = localLines.value.length > 0 ? localLines.value.length - 1 : null;
        }

        function onLineBubbleClick(idx, event) {
            if (isSelectMode.value) {
                toggleLineSelection(idx, event);
                return;
            }
            selectLine(idx);
        }

        function onTextareaFocus(idx) {
            if (!isSelectMode.value) {
                selectLine(idx);
            }
        }

        function onTextareaClick(idx, event) {
            if (!isSelectMode.value) {
                selectLine(idx);
                return;
            }
            event.target.blur();
        }

        function onEdit(idx, event) {
            if (isSelectMode.value) return;
            const newVal = event.target.value;
            const l = localLines.value[idx];
            // store 'NULL' for empty strings
            l.text = newVal.trim() === '' ? 'NULL' : newVal;
            if (l.status !== 'created') l.status = 'updated';
            l.audio_file_id = -1;
            pushBackToParent();
            emit('update:lines', localLines.value);
        }

        function clearAudio(idx) {
            const l = localLines.value[idx];
            if (!l) return;
            l.audio_file_id = -1;
            if (l.status !== 'created') l.status = 'updated';
            pushBackToParent();
            emit('update:lines', localLines.value);
        }
        function handleBackspace(idx, event) {
            if (isSelectMode.value) return;
            const curVal = event.target.value;
            if (curVal.trim() === '') {
                // only block default deletion when there's nothing left
                event.preventDefault();
                deleteLine(idx);
            }
        }

        function selectLine(idx) {
            if (isSelectMode.value) return;
            selectedIndex.value = idx;
            emit('select-line', idx);
        }

        function handleEnter(idx, event) {
            if (isSelectMode.value) return;
            event.preventDefault();
            const textarea = event.target;
            const fullText = textarea.value;
            const cursorPos = textarea.selectionStart;
            // split original text into before/after
            const before = fullText.substring(0, cursorPos);
            const after = fullText.substring(cursorPos);
            // update current line
            const orig = localLines.value[idx];
            orig.text = before.trim() === '' ? 'NULL' : before;
            if (orig.status !== 'created') orig.status = 'updated';
            // create new line object
            const newLine = {
                ...orig,
                text: after.trim() === '' ? 'NULL' : after,
                line_id: null,
                status: 'created',
                audio_file_id: -1
            };
            // insert below
            localLines.value.splice(idx + 1, 0, newLine);
            // sync out
            pushBackToParent();
            emit('update:lines', localLines.value);
            emit('select-line', idx + 1);
            // auto-resize both original and new textarea, then focus new
            nextTick(() => {
                // auto-resize original line
                textarea.style.height = 'auto';
                textarea.style.height = textarea.scrollHeight + 'px';

                // find next bubble and textarea
                const currentBubble = textarea.closest('.line-bubble');
                const nextBubble = currentBubble?.nextElementSibling;
                const nextTextarea = nextBubble?.querySelector('textarea');

                if (nextTextarea) {
                    // auto-resize new line
                    nextTextarea.style.height = 'auto';
                    nextTextarea.style.height = nextTextarea.scrollHeight + 'px';

                    // focus and place cursor
                    nextTextarea.focus();
                    nextTextarea.selectionStart = nextTextarea.selectionEnd = 0;
                }
            });
        }

        function removeLinesByIndexes(indexes) {
            const sortedIndexes = Array.from(new Set(indexes))
                .filter(idx => idx >= 0 && idx < localLines.value.length)
                .sort((a, b) => b - a);
            if (sortedIndexes.length === 0) return;

            sortedIndexes.forEach(idx => {
                localLines.value.splice(idx, 1);
            });

            if (localLines.value.length === 0) {
                selectedIndex.value = 0;
                emit('select-line', 0);
            } else {
                const firstRemovedIndex = Math.min(...sortedIndexes);
                selectedIndex.value = Math.min(firstRemovedIndex, localLines.value.length - 1);
                emit('select-line', selectedIndex.value);
            }

            exitSelectMode();
            pushBackToParent();
            emit('delete-line', sortedIndexes);
        }

        function deleteLine(idx) {
            removeLinesByIndexes([idx]);
        }

        function requestSingleDelete(idx) {
            if (props.isGenerating) return;
            if (idx < 0 || idx >= localLines.value.length) return;
            setSelectedLineIndexes([idx]);
            infoPopupMessage.value = `Delete line ${idx + 1}?`;
            showInfoPopupConfirmButton.value = true;
            showInfoPopupCancelButton.value = true;
            nextAction.value = 'delete-lines';
            showInfoPopup.value = true;
        }

        function confirmBulkDelete() {
            if (props.isGenerating) return;
            if (selectedLineIndexes.value.size === 0) return;
            const count = selectedLineIndexes.value.size;
            infoPopupMessage.value = `Delete ${count} selected ${count === 1 ? 'line' : 'lines'}?`;
            showInfoPopupConfirmButton.value = true;
            showInfoPopupCancelButton.value = true;
            nextAction.value = 'delete-lines';
            showInfoPopup.value = true;
        }

        function deleteSelectedLines() {
            removeLinesByIndexes(selectedLineIndexes.value);
        }

        function createLine() {
            localLines.value.push({
                page_id: props.pageId,
                text: '',
                audio_file_id: -1,
                status: 'created'
            });
            pushBackToParent();
            emit('create-line');
        }

        function insertLine(idx) {
            const newLine = {
                page_id: props.pageId,
                text: '',
                audio_file_id: -1,
                status: 'created'
            };
            localLines.value.splice(idx + 1, 0, newLine);
            pushBackToParent();
            selectedIndex.value = idx + 1;
            emit('select-line', idx + 1);
        }

        function playLine(idx) {
            if (isSelectMode.value) return;
            const line = localLines.value[idx];
            if (isLineActionDisabled(line, idx)) return;
            selectLine(idx);
            emit('play-line', idx);
        }

        function playAllLines() {
            if (isPageActionDisabled.value) return;
            emit('play-all-lines');
        }

        function isLineGenerating(idx) {
            return props.isGenerating && idx === props.generatingLineIndex;
        }

        function isLineTextEmpty(line) {
            const text = line?.text === 'NULL' ? '' : String(line?.text || '');
            return text.trim() === '';
        }

        function hasLineAudio(line) {
            const audioId = line?.audio_file_id;
            if (audioId === -1 || audioId === null || audioId === undefined) return false;
            const cleanAudioId = String(audioId).replace(/^"|"$/g, '').trim();
            return cleanAudioId !== '' && cleanAudioId !== '-1' && cleanAudioId.toLowerCase() !== 'null';
        }

        function lineActionLabel(line, idx) {
            if (isLineGenerating(idx)) return 'Generating';
            return hasLineAudio(line) ? 'Play' : 'Generate';
        }

        function lineActionButtonClass(line, idx) {
            return {
                'has-audio': hasLineAudio(line),
                'needs-audio': !hasLineAudio(line),
                'is-loading': isLineGenerating(idx),
            };
        }

        function isLineActionDisabled(line, idx) {
            return isLineGenerating(idx) || props.isGenerating || isLineTextEmpty(line);
        }

        function lineActionTitle(line, idx) {
            if (isLineGenerating(idx)) return `Generating audio for line ${idx + 1}`;
            if (isLineTextEmpty(line)) return 'Type text to generate audio for this line';
            return hasLineAudio(line)
                ? `Play audio for line ${idx + 1}`
                : `Generate audio for line ${idx + 1}`;
        }

        function lineActionAriaLabel(line, idx) {
            if (isLineGenerating(idx)) return `Generating audio for line ${idx + 1}`;
            if (isLineTextEmpty(line)) return `Line ${idx + 1} needs text before audio can be generated`;
            return hasLineAudio(line)
                ? `Play audio for line ${idx + 1}`
                : `Generate audio for line ${idx + 1}`;
        }

        async function loadPage() {
            try {
                const data = await getPagesByUserId();
                allPages.value = data;
                const match = data.find(p => p.page_id === props.pageId);
                currentPage.value = match;
                page_name.value = match?.page_name;
            } catch (err) {
                console.error('Failed to load page:', err);
            }
        }

        async function renameCurrentPage() {
            if (!requireAuth('EditorPanel.renameCurrentPage')) return;
            renameInput.value = currentPage.value.page_name;
            showRenamePopup.value = true;
        }
        async function onRenameConfirm({ newName }) {
            const trimmed = newName.trim();
            if (!trimmed || trimmed === currentPage.value.page_name) return;
            try {
                await modifyPageName(props.project.project_id, currentPage.value.page_id, trimmed);
                await loadPage();
                emit('page-changed');
            } catch (err) {
                infoPopupMessage.value = 'Failed to rename page. Please try again.';
                showInfoPopup.value = true;
            }
        }

        function onInfoPopupConfirm() {
            showInfoPopup.value = false;
            if (nextAction.value === 'delete') {
                deleteCurrentPage();
            } else if (nextAction.value === 'delete-lines') {
                deleteSelectedLines();
            }
            nextAction.value = '';
            showInfoPopupConfirmButton.value = false;
            showInfoPopupCancelButton.value = true;
        }
        function onInfoPopupCancel() {
            showInfoPopup.value = false;
            if (nextAction.value === 'delete-lines' && !isSelectMode.value) {
                setSelectedLineIndexes([]);
            }
            nextAction.value = '';
            showInfoPopupConfirmButton.value = false;
            showInfoPopupCancelButton.value = true;
        }
        async function onDeleteCurrentPage() {
            if (!requireAuth('EditorPanel.deleteCurrentPage')) return;
            const currentId = currentPage.value.page_id;
            const hasChildren = allPages.value.some(
                p => Number(p.parent_page_id) === currentId && p.status !== 'deleted'
            );
            if (hasChildren) {
                infoPopupMessage.value = 'This page has subpages. Please delete them first.';
                showInfoPopup.value = true;
                return;
            }
            showInfoPopup.value = true
            infoPopupMessage.value = 'Do you really want to delete this page and its content?'
            showInfoPopupConfirmButton.value = true;
            showInfoPopupCancelButton.value = true;
            nextAction.value = 'delete';
        }

        async function deleteCurrentPage() {
            try {
                await deletePageByUserId(props.project.project_id, currentPage.value.page_id);
                await props.reloadAll();
                emit('page-changed');
            } catch (err) {
                console.error('Failed to delete page', err);
                infoPopupMessage.value = 'Failed to delete page. Please try again.';
                showInfoPopup.value = true;
            }
        }

        function autoResize(event) {
            const ta = event.target;
            ta.style.height = 'auto';
            ta.style.height = ta.scrollHeight + 'px';
        }

        onMounted(async () => {
            await loadPage();
            syncFromProps();
            nextTick(() => {
                document.querySelectorAll('.line-textarea').forEach(ta => {
                    ta.style.height = 'auto';
                    ta.style.height = ta.scrollHeight + 'px';
                });
            });
        });

        watch(() => props.lines, () => syncFromProps(), { deep: true, immediate: true });
        watch(() => props.pageId, async () => {
            exitSelectMode();
            selectedIndex.value = 0;
            await loadPage();
            syncFromProps();
        });
        watch(localLines, () => {
            nextTick(() => {
                document.querySelectorAll('.line-textarea').forEach(ta => {
                    ta.style.height = 'auto';
                    ta.style.height = ta.scrollHeight + 'px';
                });
            });
        }, { deep: true });

        return {
            page_name,
            currentPage,
            localLines,
            selectedIndex,
            activeLine,
            totalChars,
            pageActionLabel,
            pageActionButtonClass,
            isPageActionDisabled,
            pageActionTitle,
            pageActionAriaLabel,
            selectedCount,
            allLinesSelected,
            isSelectMode,
            isLineSelected,
            enterSelectMode,
            exitSelectMode,
            toggleLineSelection,
            toggleSelectAll,
            onLineBubbleClick,
            onTextareaFocus,
            onTextareaClick,
            requestSingleDelete,
            confirmBulkDelete,
            onEdit,
            selectLine,
            deleteLine,
            createLine,
            playLine,
            playAllLines,
            clearAudio,
            hasLineAudio,
            isLineGenerating,
            lineActionLabel,
            lineActionButtonClass,
            isLineActionDisabled,
            lineActionTitle,
            lineActionAriaLabel,
            renameCurrentPage,
            deleteCurrentPage,
            insertLine,
            handleBackspace,
            handleEnter,
            autoResize,
            showRenamePopup,
            renameInput,
            onRenameConfirm,
            infoPopupMessage,
            showInfoPopup,
            showInfoPopupConfirmButton,
            showInfoPopupCancelButton,
            onInfoPopupConfirm,
            onInfoPopupCancel,
            onDeleteCurrentPage,
        };
    },
});
</script>
<style scoped>
.editor-panel-inner {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    min-width: 0;
}

.editor-header {
    padding: 1rem;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
    border-bottom: 1px solid var(--border-color);
}

.editor-header h3 {
    font-size: 1.1rem;
    color: var(--text-secondary);
    font-weight: 500;
    margin: 0;
    line-height: 1.2;
    display: flex;
    align-items: center;
}

.modify-page-btn {
    background: transparent;
    border: none;
    border-radius: 20%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--grey);
    transition: background 0.2s;
    margin-right: 8px;
}

.modify-page-btn:hover {
    background: #e0e5eb;
}

.delete-page-btn {
    background: transparent;
    border: none;
    border-radius: 20%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--grey);
    transition: background 0.2s;
    margin-left: auto;
}

.delete-page-btn:hover {
    background: #e0e5eb;
}

.save-indicator {
    font-size: 0.8rem;
    color: var(--text-secondary);
    margin-left: 0.5rem;
    line-height: 1.2;
    display: flex;
    align-items: center;
}


.textarea-toolbar {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem;
    border-top: 1px solid var(--border-color);
    flex-wrap: wrap;
}


.toolbar-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-left: auto;
    flex-wrap: wrap;
    justify-content: flex-end;
}

.char-count {
    font-size: 0.9rem;
    color: var(--text-secondary);
}

.toolbar-btn {
    min-height: 34px;
    padding: 7px 12px;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    background: var(--background-white);
    color: var(--text-secondary);
    cursor: pointer;
    font-family: 'Inter', sans-serif;
    font-size: 0.9rem;
    line-height: 1;
    transition: background-color 0.2s, border-color 0.2s, color 0.2s;
}

.toolbar-btn:hover:not(:disabled) {
    border-color: var(--purple);
    color: var(--purple);
    background: rgba(128, 0, 128, 0.06);
}

.toolbar-btn.danger {
    color: #b42318;
    border-color: #f3c6c2;
}

.toolbar-btn.danger:hover:not(:disabled) {
    background: #fff2f1;
    border-color: #d92d20;
    color: #d92d20;
}

.toolbar-btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}

.selected-count {
    color: var(--text-secondary);
    font-size: 0.9rem;
    white-space: nowrap;
}

.btn {
    font-family: 'Poppins', sans-serif;
    text-decoration: none;
    padding: 12px 24px;
    border-radius: 8px;
    font-weight: 500;
    transition: all 0.3s ease;
    display: inline-block;
    border: none;
    cursor: pointer;
}

.btn-generate {
    background-color: var(--accent-indigo);
    color: white;
    padding: 10px 20px;
}

.empty-page-message {
    flex-grow: 1;
    overflow-y: auto;
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    margin: 10px;
}

.cm-wrapper {
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;
    border: 1px solid var(--border-color);
    border-radius: 8px;
    box-sizing: border-box;
    overflow: hidden;
}

.cm-editor {
    height: 100%;
}

.cm-scroller {
    overflow: auto;
}

:deep(.cm-audio-gutter .has-audio) {
    font-weight: 500;
}

:deep(.cm-audio-gutter .cm-gutterElement) {
    font-variant-numeric: tabular-nums;
    user-select: none;
    cursor: pointer;
}

.line-list {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.line-bubble {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px 14px 14px;
    background: var(--background-white);
    border: 1px solid var(--border-color);
    border-radius: 12px;
    cursor: pointer;
    transition: border-color 0.2s, box-shadow 0.2s, background-color 0.2s;
}

.line-bubble.selected {
    box-shadow: 0 0 0 2px var(--purple);
}

.line-bubble.bulk-selected {
    border-color: var(--purple);
    background: rgba(128, 0, 128, 0.06);
}

.line-bubble.select-mode .line-textarea {
    cursor: pointer;
}

.line-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 30px;
}

.line-toolbar-meta,
.line-toolbar-actions {
    display: flex;
    align-items: center;
}

.line-toolbar-meta {
    gap: 8px;
    min-width: 0;
}

.line-toolbar-actions {
    gap: 8px;
    flex-wrap: wrap;
    justify-content: flex-end;
    min-height: 30px;
}

.line-check {
    width: 22px;
    height: 22px;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    background: var(--background-white);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background-color 0.2s, border-color 0.2s;
}

.line-check.checked {
    background: var(--purple);
    border-color: var(--purple);
}

.line-check:focus-visible,
.toolbar-btn:focus-visible,
.line-card-action:focus-visible {
    outline: 2px solid var(--purple);
    outline-offset: 2px;
}

.line-content {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    width: 100%;
    min-width: 0;
}

.ln-num {
    position: static;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    background: #f7f7fb;
    padding: 2px 6px;
    font-size: 12px;
    color: var(--text-secondary);
    line-height: 1;
    border-radius: 999px;
}

.line-card-action {
    min-height: 30px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    flex: none;
    padding: 4px 10px;
    cursor: pointer;
    border: 1px solid currentColor;
    border-radius: 999px;
    font-family: 'Inter', sans-serif;
    font-size: 0.78rem;
    font-weight: 600;
    line-height: 1;
    white-space: nowrap;
    transition: background-color 0.2s, border-color 0.2s, color 0.2s, opacity 0.2s;
}

.line-main-action {
    min-width: 86px;
}

.toolbar-line-action {
    min-width: 96px;
}

.page-all-action {
    min-width: 112px;
}

.line-main-action.needs-audio {
    background: var(--purple);
    border-color: var(--purple);
    color: #fff;
}

.line-main-action.has-audio {
    background: #ecfdf5;
    border-color: #99f6e4;
    color: #047857;
}

.line-main-action.is-loading {
    background: #f3f0ff;
    border-color: #d8ccff;
    color: var(--purple);
}

.line-main-action.needs-audio:hover:not(:disabled) {
    background: var(--purple-hover);
    border-color: var(--purple-hover);
}

.line-main-action.has-audio:hover:not(:disabled) {
    background: #dffcf0;
    border-color: #5eead4;
    color: #065f46;
}

.line-card-action:disabled {
    cursor: not-allowed;
    opacity: 0.52;
}

.line-main-action.is-loading:disabled {
    opacity: 1;
}

.play-icon {
    display: block;
    width: 15px;
    height: 15px;
    flex: 0 0 15px;
    margin-left: 1px;
}

.line-action-label {
    min-width: 0;
}

.line-textarea {
    border: none;
    outline: none;
    background: transparent;
    width: 100%;
    min-width: 0;
    resize: none;
    font-size: 1rem;
    line-height: 1.5;
    font-family: 'Inter', sans-serif;
    color: var(--text-primary);
    overflow: hidden;
    height: auto;
}

.new-line-btn {
    margin-top: auto;
    margin: 0.75rem;
    padding: 8px 16px;
    font-size: 1rem;
    background-color: #F2F4F7;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.2s;
}

.insert-btn {
    border: none;
    background: transparent;
    cursor: pointer;
    font-size: 14px;
    padding: 2px 4px;
    line-height: 1;
    color: var(--accent-indigo);
}

.spinner-line {
    width: 15px;
    height: 15px;
    flex: 0 0 15px;
    border: 2px solid rgba(128, 0, 128, 0.2);
    border-top-color: var(--purple);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

.audio-ready-pill {
    padding: 4px 6px 4px 10px;
    border: 1px solid #e5c3c8;
    background: #fffafa;
    color: #8a3942;
}

.audio-ready-remove {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #8a3942;
    background: #f7e7e9;
    transition: background-color 0.2s, color 0.2s;
}

.audio-ready-remove svg {
    width: 12px;
    height: 12px;
}

.audio-ready-pill:hover,
.audio-ready-pill:focus-visible {
    border-color: #d8aab1;
    background: #fff5f6;
    color: #7a2f38;
}

.audio-ready-pill:hover .audio-ready-remove,
.audio-ready-pill:focus-visible .audio-ready-remove {
    background: #efd6da;
    color: #7a2f38;
}

.audio-ready-pill:focus-visible {
    outline: 2px solid var(--purple);
    outline-offset: 2px;
}

.delete-line-action {
    background: #fff7f7;
    border-color: #f3c6c2;
    color: #b42318;
    opacity: 0;
    pointer-events: none;
}

.delete-line-action svg {
    width: 14px;
    height: 14px;
}

.delete-line-action:hover:not(:disabled) {
    background: #fff2f1;
    border-color: #d92d20;
    color: #d92d20;
}

.line-bubble:hover .delete-line-action,
.line-bubble:focus-within .delete-line-action {
    opacity: 1;
    pointer-events: auto;
}

@media (max-width: 640px) {
    .textarea-toolbar {
        align-items: stretch;
    }

    .toolbar-actions {
        width: 100%;
        margin-left: 0;
        justify-content: flex-start;
    }

    .toolbar-btn {
        flex: 1 1 auto;
    }

    .line-bubble {
        gap: 8px;
        padding: 10px;
    }

    .line-toolbar {
        align-items: flex-start;
        flex-direction: column;
    }

    .line-toolbar-actions {
        width: 100%;
        justify-content: flex-start;
    }

    .delete-line-action {
        opacity: 1;
        pointer-events: auto;
    }
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}
</style>
